import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Brain,
  GitFork,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { useProblemSearch } from '../hooks/use-problem-search'
import { useCoachingQuestions } from '../hooks/use-questions'
import { useRelationships } from '../hooks/use-relationships'
import type { ProblemEditorData } from '../problem-editor'
import type {
  CoachingQuestion,
  ProblemRelationship,
  ProblemRelationshipType,
  RelationshipStrength,
} from '../types'
import { getRelationshipSentence } from '../types'

interface ProblemDependenciesSectionProps {
  formData: ProblemEditorData
}

// Relationship type metadata for display
const RELATIONSHIP_TYPES: Record<
  ProblemRelationshipType,
  {
    label: string
    icon: React.ComponentType<{ className?: string }>
    color: string
    description: string
  }
> = {
  prerequisite: {
    label: 'Prerequisite',
    icon: Brain,
    color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/50',
    description: 'Understanding target helps before attempting source',
  },
  builds_on: {
    label: 'Builds On',
    icon: Layers,
    color:
      'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/50',
    description: "Source's solution extends target's solution",
  },
  harder_than: {
    label: 'Harder Than',
    icon: TrendingUp,
    color:
      'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/50',
    description: 'Source is target with harder constraints',
  },
}

// Strength metadata for display
const STRENGTH_BADGES: Record<
  RelationshipStrength,
  { label: string; color: string }
> = {
  required: {
    label: 'Required',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
  },
  recommended: {
    label: 'Recommended',
    color:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400',
  },
  optional: {
    label: 'Optional',
    color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  },
}

export function ProblemDependenciesSection({
  formData,
}: ProblemDependenciesSectionProps) {
  // For creating relationships with swapped direction (selected problem as source)
  const swappedCreateMutation = trpc.admin.relationships.create.useMutation()

  // Problem relationships hook
  const {
    relationships,
    loading: relationshipsLoading,
    loadRelationships,
    createRelationship,
    updateRelationship,
    deleteRelationship,
    swapRelationshipDirection,
  } = useRelationships(formData.id)

  // For problem search in add relationship popover
  const { questions } = useCoachingQuestions()
  const { search, minSearchLength } = useProblemSearch(questions)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false)
  const [selectedProblem, setSelectedProblem] =
    useState<CoachingQuestion | null>(null)
  const [newRelType, setNewRelType] =
    useState<ProblemRelationshipType>('prerequisite')
  const [newRelStrength, setNewRelStrength] =
    useState<RelationshipStrength>('recommended')
  const [newRelNotes, setNewRelNotes] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [isDirectionSwapped, setIsDirectionSwapped] = useState(false)

  // For editing relationships
  const [editingRelId, setEditingRelId] = useState<string | null>(null)
  const [editRelType, setEditRelType] =
    useState<ProblemRelationshipType>('prerequisite')
  const [editRelStrength, setEditRelStrength] =
    useState<RelationshipStrength>('recommended')
  const [editRelNotes, setEditRelNotes] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  // Search results filtered to exclude current problem and already-related problems
  const searchResults = useMemo(() => {
    if (searchQuery.length < minSearchLength) return []
    const results = search(searchQuery)
    const relatedProblemIds = new Set(
      relationships.map((r) =>
        r.direction === 'outgoing' ? r.targetProblemId : r.sourceProblemId,
      ),
    )
    return results.filter(
      (r) => r.item.id !== formData.id && !relatedProblemIds.has(r.item.id),
    )
  }, [searchQuery, minSearchLength, search, formData.id, relationships])

  // Group relationships by direction
  const outgoingRelationships = useMemo(
    () => relationships.filter((r) => r.direction === 'outgoing'),
    [relationships],
  )
  const incomingRelationships = useMemo(
    () => relationships.filter((r) => r.direction === 'incoming'),
    [relationships],
  )

  // Load relationships on mount
  useEffect(() => {
    if (formData.id) {
      loadRelationships()
    }
  }, [formData.id, loadRelationships])

  // Create new relationship
  const handleCreateRelationship = useCallback(async () => {
    if (!selectedProblem || !formData.id) return

    setIsCreating(true)
    try {
      if (isDirectionSwapped) {
        // When swapped: selected problem is source, current problem is target
        await swappedCreateMutation.mutateAsync({
          sourceProblemId: selectedProblem.id,
          targetProblemId: formData.id,
          type: newRelType,
          strength: newRelStrength,
          notes: newRelNotes.trim() || null,
        })

        // Reload relationships to see the new incoming relationship
        await loadRelationships()
      } else {
        // Normal case: current problem is source, selected is target
        await createRelationship({
          targetProblemId: selectedProblem.id,
          type: newRelType,
          strength: newRelStrength,
          notes: newRelNotes.trim() || null,
        })
      }

      toast.success('Relationship created')
      setIsAddPopoverOpen(false)
      setSelectedProblem(null)
      setSearchQuery('')
      setNewRelType('prerequisite')
      setNewRelStrength('recommended')
      setNewRelNotes('')
      setIsDirectionSwapped(false)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to create relationship',
      )
    } finally {
      setIsCreating(false)
    }
  }, [
    selectedProblem,
    formData.id,
    isDirectionSwapped,
    newRelType,
    newRelStrength,
    newRelNotes,
    createRelationship,
    swappedCreateMutation,
    loadRelationships,
  ])

  // Start editing a relationship
  const handleStartEditingRel = useCallback((rel: ProblemRelationship) => {
    setEditingRelId(rel.id)
    setEditRelType(rel.type)
    setEditRelStrength(rel.strength)
    setEditRelNotes(rel.notes || '')
  }, [])

  // Save edited relationship
  const handleSaveRelationship = useCallback(async () => {
    if (!editingRelId) return

    setIsUpdating(true)
    try {
      await updateRelationship(editingRelId, {
        type: editRelType,
        strength: editRelStrength,
        notes: editRelNotes.trim() || null,
      })

      toast.success('Relationship updated')
      setEditingRelId(null)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update relationship',
      )
    } finally {
      setIsUpdating(false)
    }
  }, [
    editingRelId,
    editRelType,
    editRelStrength,
    editRelNotes,
    updateRelationship,
  ])

  // Delete relationship
  const handleDeleteRelationship = useCallback(
    async (relationshipId: string) => {
      // eslint-disable-next-line no-alert -- Intentional use for confirmation dialog
      const confirmed = window.confirm(
        'Are you sure you want to delete this relationship?',
      )
      if (!confirmed) return

      try {
        await deleteRelationship(relationshipId)
        toast.success('Relationship deleted')
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Failed to delete relationship',
        )
      }
    },
    [deleteRelationship],
  )

  // Swap relationship direction (source <-> target)
  const handleSwapDirection = useCallback(
    async (relationshipId: string) => {
      try {
        await swapRelationshipDirection(relationshipId)
        toast.success('Relationship direction swapped')
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : 'Failed to swap direction',
        )
      }
    },
    [swapRelationshipDirection],
  )

  // Render a single relationship card
  const renderRelationshipCard = (rel: ProblemRelationship) => {
    const typeInfo = RELATIONSHIP_TYPES[rel.type]
    const strengthInfo = STRENGTH_BADGES[rel.strength]
    const TypeIcon = typeInfo.icon
    const isEditing = editingRelId === rel.id

    if (isEditing) {
      return (
        <div key={rel.id} className="border-primary rounded-lg border-2 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-medium">Edit Relationship</span>
            <button
              type="button"
              onClick={() => setEditingRelId(null)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Cancel editing"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Type selector */}
          <div className="mb-3">
            <label className="text-muted-foreground mb-1 block text-xs font-medium">
              Relationship Type
            </label>
            <select
              value={editRelType}
              onChange={(e) =>
                setEditRelType(e.target.value as ProblemRelationshipType)
              }
              className="bg-background w-full rounded border px-3 py-2 text-sm"
            >
              {Object.entries(RELATIONSHIP_TYPES).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Strength selector */}
          <div className="mb-3">
            <label className="text-muted-foreground mb-1 block text-xs font-medium">
              Strength
            </label>
            <select
              value={editRelStrength}
              onChange={(e) =>
                setEditRelStrength(e.target.value as RelationshipStrength)
              }
              className="bg-background w-full rounded border px-3 py-2 text-sm"
            >
              {Object.entries(STRENGTH_BADGES).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="mb-3">
            <label className="text-muted-foreground mb-1 block text-xs font-medium">
              Notes (optional)
            </label>
            <textarea
              value={editRelNotes}
              onChange={(e) => setEditRelNotes(e.target.value)}
              className="bg-background w-full rounded border px-3 py-2 text-sm"
              rows={2}
              placeholder="Add context about this relationship..."
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditingRelId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSaveRelationship}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      )
    }

    // Get the source and target names for the sentence
    const sourceName =
      rel.direction === 'outgoing' ? formData.name : rel.relatedProblem.title
    const targetName =
      rel.direction === 'outgoing' ? rel.relatedProblem.title : formData.name
    const sentence = getRelationshipSentence(
      rel.type,
      sourceName,
      targetName,
      rel.direction,
      formData.name,
    )

    return (
      <div
        key={rel.id}
        className="group rounded-lg border p-3 transition-colors hover:border-gray-400 dark:hover:border-gray-600"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Type and strength badges */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${typeInfo.color}`}
              >
                <TypeIcon className="h-3 w-3" />
                {typeInfo.label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${strengthInfo.color}`}
              >
                {strengthInfo.label}
              </span>
            </div>

            {/* Clear relationship sentence with arrow: Target → Source */}
            <div className="flex flex-wrap items-center gap-2 font-medium">
              <span
                className={rel.direction === 'incoming' ? 'text-primary' : ''}
              >
                {targetName}
                {rel.direction === 'incoming' && (
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    (this)
                  </span>
                )}
              </span>
              <ArrowRight className="text-muted-foreground h-4 w-4 flex-shrink-0" />
              <span
                className={rel.direction === 'outgoing' ? 'text-primary' : ''}
              >
                {sourceName}
                {rel.direction === 'outgoing' && (
                  <span className="text-muted-foreground ml-1 text-xs font-normal">
                    (this)
                  </span>
                )}
              </span>
            </div>

            {/* Clarification */}
            <p className="text-muted-foreground mt-1 text-sm">
              {sentence.clarification}
            </p>

            {/* Notes */}
            {rel.notes && (
              <p className="text-muted-foreground mt-1 text-sm italic">
                "{rel.notes}"
              </p>
            )}
          </div>

          {/* Actions */}
          <div className={cn(REVEAL_ON_HOVER, 'flex items-center gap-1')}>
            <button
              type="button"
              onClick={() => handleSwapDirection(rel.id)}
              className="text-muted-foreground hover:text-primary rounded p-1"
              title="Swap direction"
              aria-label="Swap relationship direction"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleStartEditingRel(rel)}
              className="text-muted-foreground hover:text-foreground rounded p-1"
              title="Edit relationship"
              aria-label="Edit relationship"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteRelationship(rel.id)}
              className="text-muted-foreground hover:text-destructive rounded p-1"
              title="Delete relationship"
              aria-label="Delete relationship"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!formData.id) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">
          Save the problem first to manage dependencies.
        </p>
      </div>
    )
  }

  if (relationshipsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <GitFork className="h-5 w-5" />
            Problem Dependencies
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Connect problems based on learning order and solution dependencies.
          </p>
        </div>
        <Popover open={isAddPopoverOpen} onOpenChange={setIsAddPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="default" size="sm">
              <Plus className="mr-1 h-4 w-4" />
              Add Relationship
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-4" align="end">
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 font-medium">Add Problem Relationship</h4>
                <p className="text-muted-foreground text-xs">
                  Create a dependency link from this problem to another.
                </p>
              </div>

              {/* Problem search */}
              {!selectedProblem ? (
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    Search for a problem
                  </label>
                  <div className="relative">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type to search problems..."
                      className="bg-background w-full rounded border py-2 pr-3 pl-9 text-sm"
                    />
                  </div>
                  {searchQuery.length > 0 &&
                    searchQuery.length < minSearchLength && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        Type at least {minSearchLength} characters to search
                      </p>
                    )}
                  {searchResults.length > 0 && (
                    <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded border">
                      {searchResults.map((result) => (
                        <button
                          key={result.item.id}
                          type="button"
                          onClick={() => {
                            setSelectedProblem(result.item)
                            setSearchQuery('')
                          }}
                          className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
                        >
                          {result.item.externalId
                            ? `#${result.item.externalId} - `
                            : ''}
                          {result.item.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {searchQuery.length >= minSearchLength &&
                    searchResults.length === 0 && (
                      <p className="text-muted-foreground mt-2 text-center text-xs">
                        No problems found
                      </p>
                    )}
                </div>
              ) : (
                <>
                  {/* Selected problem */}
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Target Problem
                    </label>
                    <div className="bg-muted flex items-center justify-between rounded px-3 py-2">
                      <span className="text-sm font-medium">
                        {selectedProblem.externalId
                          ? `#${selectedProblem.externalId} - `
                          : ''}
                        {selectedProblem.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedProblem(null)
                          setIsDirectionSwapped(false)
                        }}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Clear selection"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Type selector */}
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Relationship Type
                    </label>
                    <select
                      value={newRelType}
                      onChange={(e) =>
                        setNewRelType(e.target.value as ProblemRelationshipType)
                      }
                      className="bg-background w-full rounded border px-3 py-2 text-sm"
                    >
                      {Object.entries(RELATIONSHIP_TYPES).map(
                        ([key, { label, description }]) => (
                          <option key={key} value={key}>
                            {label} - {description}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  {/* Strength selector */}
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Strength
                    </label>
                    <select
                      value={newRelStrength}
                      onChange={(e) =>
                        setNewRelStrength(
                          e.target.value as RelationshipStrength,
                        )
                      }
                      className="bg-background w-full rounded border px-3 py-2 text-sm"
                    >
                      <option value="required">
                        Required - Must complete first
                      </option>
                      <option value="recommended">
                        Recommended - Should complete first
                      </option>
                      <option value="optional">Optional - Nice to have</option>
                    </select>
                  </div>

                  {/* Preview sentence with swap button */}
                  {selectedProblem &&
                    (() => {
                      const previewSource = isDirectionSwapped
                        ? selectedProblem.name
                        : formData.name
                      const previewTarget = isDirectionSwapped
                        ? formData.name
                        : selectedProblem.name
                      const direction = isDirectionSwapped
                        ? 'incoming'
                        : 'outgoing'
                      const sentence = getRelationshipSentence(
                        newRelType,
                        previewSource,
                        previewTarget,
                        direction,
                        formData.name,
                      )
                      return (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 font-medium text-blue-900 dark:text-blue-100">
                              <span>{previewTarget}</span>
                              <ArrowRight className="h-4 w-4 flex-shrink-0" />
                              <span>{previewSource}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setIsDirectionSwapped(!isDirectionSwapped)
                              }
                              className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-200 dark:text-blue-400 dark:hover:bg-blue-800"
                              title="Swap direction"
                              aria-label="Swap relationship direction"
                            >
                              <ArrowLeftRight className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                            {sentence.clarification}
                          </p>
                        </div>
                      )
                    })()}

                  {/* Notes */}
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs font-medium">
                      Notes (optional)
                    </label>
                    <textarea
                      value={newRelNotes}
                      onChange={(e) => setNewRelNotes(e.target.value)}
                      className="bg-background w-full rounded border px-3 py-2 text-sm"
                      rows={2}
                      placeholder="Add context about this relationship..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsAddPopoverOpen(false)
                        setSelectedProblem(null)
                        setIsDirectionSwapped(false)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateRelationship}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {relationships.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            No problem dependencies defined.
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Click "Add Relationship" to connect this problem to others.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Outgoing relationships */}
          {outgoingRelationships.length > 0 && (
            <div>
              <h4 className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                <ArrowRight className="h-4 w-4" />
                This problem depends on ({outgoingRelationships.length})
              </h4>
              <div className="space-y-2">
                {outgoingRelationships.map(renderRelationshipCard)}
              </div>
            </div>
          )}

          {/* Incoming relationships */}
          {incomingRelationships.length > 0 && (
            <div>
              <h4 className="text-muted-foreground mb-2 flex items-center gap-2 text-sm font-medium">
                <ArrowLeft className="h-4 w-4" />
                Problems that depend on this ({incomingRelationships.length})
              </h4>
              <div className="space-y-2">
                {incomingRelationships.map(renderRelationshipCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
